// @ts-strict-ignore
import { useEffect, useState } from 'react';
import styled from 'styled-components';
import { useDispatch, useSelector } from 'src/store/store';
import i18n from 'src/utils/translate';
import { helpScoutArticle } from 'src/utils/helpScoutBeacon';
import { setShortEnrichedDescription } from 'src/modules/community/communityAbout/actions';
import AboutEditor from 'src/modules/community/communityAbout/components/aboutEditor';
import AboutEditorAndroid from 'src/modules/community/communityAbout/components/aboutEditorAndroid';
import SpinnerOverlay, { Size } from 'src/components/spinnerOverlay';
import Icon from 'src/components/icon';
import GetElement from 'src/components/getElement';
import { LinkButton } from 'styles-js/buttons';
import { textWideSpacing } from 'styles-js/mixins/typography';

const DESCRIPTION_MAX_LENGTH = 2000;

export default function Description() {
  const dispatch = useDispatch();
  const { description, enrichedDescription, enrichedDescriptionShort, isEditingDescription,
    isLoading } = useSelector(({ pages }) => pages.communityAbout);
  const { currentCommunity, isAndroidUser } = useSelector(({ context }) => context);
  const [isShortened, setIsShortened] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  const defaultDescription = currentCommunity?.name + i18n.t(' is a community dedicated to providing reliable information and support to its members.');
  const shortenText =  (text: string) => text.slice(0, DESCRIPTION_MAX_LENGTH - 1) + '...';

  useEffect(() => {
    if (description?.length > DESCRIPTION_MAX_LENGTH) {
      setIsShortened(true);
      dispatch(setShortEnrichedDescription(shortenText(description)));
    }
  }, []); // eslint-disable-line

  return (
    <Container>
      {isEditingDescription ? (
        <>
          <Disclaimer>
            {i18n.t('The community description can help your community grow.')}{' '}
            <LinkButton onClick={() => helpScoutArticle('134-community-description')}>{i18n.t('Learn more')}{' >'}</LinkButton>
          </Disclaimer>

          {isLoading && <SpinnerOverlay margin={'0 auto'} size={Size.S} wholePage={false} />}
          {isAndroidUser ? <AboutEditorAndroid /> : <AboutEditor />}
        </>
      ) : (
        enrichedDescription ? (
          <>
            <TextWrapper isVisible={!isShortened || isShortened && isExpanded}>
              <GetElement enrichedContent={enrichedDescription} />
            </TextWrapper>
            {enrichedDescriptionShort && (
              <TextWrapper isVisible={isShortened && !isExpanded}>
                <GetElement enrichedContent={enrichedDescriptionShort} />
              </TextWrapper>
            )}
            {isShortened && (
              <LinkButton onClick={() => setIsExpanded(!isExpanded)}>
                {isExpanded ? i18n.t('Read less') : i18n.t('Read more')} <Icon icon={isExpanded ? 'open-up' : 'open-down'} />
              </LinkButton>
            )}
          </>
        ) : (
          <p>{defaultDescription}</p>
        )
      )}
    </Container>
  );
}

const Disclaimer = styled.p`
  background-color: ${({ theme }) => theme.colorBlueBackground};
  padding: 20px;
  margin-bottom: 15px;
`;

const Container = styled.div`
  position: relative;
  ${textWideSpacing};
  white-space: pre-wrap;
  margin-bottom: 45px;
`;

const TextWrapper = styled.div<{ isVisible: boolean }>`
  ${({ isVisible }) => isVisible ? 'display: block' : 'display: none'};
`;
